import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const LANGUAGES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'c', label: 'C' },
  { value: 'csharp', label: 'C#' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'bash', label: 'Bash' },
  { value: 'json', label: 'JSON' },
  { value: 'yaml', label: 'YAML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'text', label: 'Plain Text' }
];

function RichTextEditor({ value, onChange }) {
  const [activeTab, setActiveTab] = useState('edit');
  const [showCodeHelper, setShowCodeHelper] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');

  const insertCodeBlock = () => {
    const codeBlock = `\n\`\`\`${selectedLanguage}\n// Your code here\n\`\`\`\n`;
    onChange(value + codeBlock);
    setShowCodeHelper(false);
  };

  const insertFormatting = (format) => {
    const textarea = document.getElementById('markdown-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = '';

    switch (format) {
      case 'bold':
        newText = value.substring(0, start) + `**${selectedText || 'bold text'}**` + value.substring(end);
        break;
      case 'italic':
        newText = value.substring(0, start) + `*${selectedText || 'italic text'}*` + value.substring(end);
        break;
      case 'heading':
        newText = value.substring(0, start) + `\n## ${selectedText || 'Heading'}\n` + value.substring(end);
        break;
      case 'list':
        newText = value.substring(0, start) + `\n- ${selectedText || 'List item'}\n` + value.substring(end);
        break;
      case 'link':
        newText = value.substring(0, start) + `[${selectedText || 'link text'}](url)` + value.substring(end);
        break;
      case 'quote':
        newText = value.substring(0, start) + `\n> ${selectedText || 'Quote'}\n` + value.substring(end);
        break;
      case 'table':
        const tableTemplate = `\n| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Row 1 Col 1 | Row 1 Col 2 | Row 1 Col 3 |\n| Row 2 Col 1 | Row 2 Col 2 | Row 2 Col 3 |\n`;
        newText = value.substring(0, start) + tableTemplate + value.substring(end);
        break;
      default:
        return;
    }

    onChange(newText);
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f7fafc' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab('edit');
          }}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: activeTab === 'edit' ? '#ffffff' : 'transparent',
            borderBottom: activeTab === 'edit' ? '2px solid #667eea' : 'none',
            color: activeTab === 'edit' ? '#667eea' : '#718096',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          ✏️ Edit
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab('preview');
          }}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: activeTab === 'preview' ? '#ffffff' : 'transparent',
            borderBottom: activeTab === 'preview' ? '2px solid #667eea' : 'none',
            color: activeTab === 'preview' ? '#667eea' : '#718096',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          👁️ Preview
        </button>
      </div>

      {activeTab === 'edit' ? (
        <>
          {/* Formatting Toolbar */}
          <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '4px', flexWrap: 'wrap', background: '#fafafa' }}>
            <button
              type="button"
              onClick={() => insertFormatting('bold')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('italic')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Italic"
            >
              <em>I</em>
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('heading')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Heading"
            >
              H
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('list')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="List"
            >
              •
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('link')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Link"
            >
              🔗
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('quote')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Quote"
            >
              "
            </button>
            <button
              type="button"
              onClick={() => insertFormatting('table')}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#ffffff', cursor: 'pointer', fontSize: '13px' }}
              title="Insert Table"
            >
              ⊞
            </button>
            <button
              type="button"
              onClick={() => setShowCodeHelper(!showCodeHelper)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: showCodeHelper ? '#667eea' : '#ffffff', color: showCodeHelper ? '#ffffff' : '#1a202c', cursor: 'pointer', fontSize: '13px' }}
              title="Insert Code Block"
            >
              {'</>'}
            </button>
          </div>

          {/* Code Block Helper */}
          {showCodeHelper && (
            <div style={{ padding: '12px', background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '13px', color: '#4a5568', fontWeight: '600' }}>Language:</label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }}
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={insertCodeBlock}
                  className="btn btn-primary"
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  Insert Code Block
                </button>
                <button
                  type="button"
                  onClick={() => setShowCodeHelper(false)}
                  className="btn btn-secondary"
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  Cancel
                </button>
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#718096' }}>
                💡 Tip: Paste code from ChatGPT/Perplexity and wrap with ```{selectedLanguage} ... ```
              </p>
            </div>
          )}

          {/* Editor */}
          <textarea
            id="markdown-editor"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Write or paste your content here... Supports Markdown formatting, code blocks, and rich text from LLMs."
            style={{
              width: '100%',
              minHeight: '300px',
              padding: '16px',
              border: 'none',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.6',
              resize: 'vertical',
              outline: 'none',
              color: '#1a202c'
            }}
          />

          {/* Help Text */}
          <div style={{ padding: '8px 16px', background: '#f7fafc', borderTop: '1px solid #e2e8f0' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
              📝 Supports: **bold**, *italic*, ## headings, - lists, [links](url), code blocks, and more
            </p>
          </div>
        </>
      ) : (
        <div style={{ padding: '16px', minHeight: '300px', maxHeight: '500px', overflow: 'auto' }}>
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props} style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {value || '*No content to preview*'}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

export default RichTextEditor;
